import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { money, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, CheckCircle2, Truck, CreditCard, FileText, XCircle, DollarSign } from "lucide-react";

const STATUSES = ["Placed", "Confirmed", "Preparing", "Ready", "Shipped", "Completed", "Cancelled"];
const PAY_LABEL = { pending_approval: "Pending Approval", partially_paid: "Partially Paid", paid: "Paid", rejected: "Rejected", unpaid: "Unpaid", due: "Due" };

export default function OrderDetail() {
  const { oid } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [partial, setPartial] = useState(false);
  const [reject, setReject] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [ownerNotes, setOwnerNotes] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/store/orders/${oid}`);
    setO(data); setOwnerNotes(data.owner_notes || "");
  }, [oid]);
  useEffect(() => { load(); }, [load]);
  if (!o) return <FullScreenLoader />;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };
  const changeStatus = (status) => act(() => api.patch(`/store/orders/${oid}/status`, { status }), `Status → ${status}`);
  const approve = () => act(() => api.post(`/store/orders/${oid}/payment/approve`), "Payment approved");
  const markPaid = () => act(() => api.patch(`/store/orders/${oid}/mark-paid`), "Payment collected");
  const doPartial = () => act(async () => { await api.post(`/store/orders/${oid}/payment/partial`, { confirmed_amount: Number(amount) }); setPartial(false); }, "Partial payment recorded");
  const doReject = () => { if (!reason.trim()) { toast.error("Reason required"); return; } act(async () => { await api.post(`/store/orders/${oid}/payment/reject`, { reason }); setReject(false); setReason(""); }, "Payment rejected"); };
  const saveNotes = () => act(() => api.patch(`/store/orders/${oid}/notes`, { owner_notes: ownerNotes }), "Notes saved");

  const isCod = o.payment_method === "cod";
  const ps = o.payment_status;
  const canReview = !isCod && (ps === "pending_approval" || ps === "partially_paid" || ps === "rejected");

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate("/dashboard/orders")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-orders"><ArrowLeft className="h-4 w-4" /> Back to orders</button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{o.order_number}</h1>
            <StatusBadge status={o.status} />
          </div>
          <p className="text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3">
          {isCod && ps !== "paid" && <Button onClick={markPaid} className="rounded-lg" data-testid="mark-paid-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark as collected</Button>}
          <Select value={STATUSES.includes(o.status) ? o.status : undefined} onValueChange={changeStatus}>
            <SelectTrigger className="h-10 w-48" data-testid="order-status-select"><SelectValue placeholder={o.status} /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-xl border-border overflow-hidden">
            <div className="p-5 border-b border-border"><h3 className="font-semibold">Items</h3></div>
            <div className="divide-y divide-border">
              {o.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between p-4">
                  <div><p className="font-medium text-sm">{it.name}</p><p className="text-xs text-muted-foreground">{money(it.price)} × {it.quantity}</p></div>
                  <span className="font-semibold text-sm">{money(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-border space-y-2 bg-secondary/30">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{money(o.subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground capitalize">{o.fulfillment_method} fee</span><span>{money(o.fee)}</span></div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-border"><span>Total</span><span>{money(o.total)}</span></div>
            </div>
          </Card>

          {o.proof_file_path && (
            <Card className="rounded-xl border-border p-5">
              <div className="flex items-center gap-2 mb-3"><FileText className="h-4 w-4 text-muted-foreground" /><h3 className="font-semibold">Payment proof</h3></div>
              {o.proof_file_path.toLowerCase().endsWith(".pdf")
                ? <a href={fileUrl(o.proof_file_path)} target="_blank" rel="noreferrer" className="text-primary underline text-sm" data-testid="proof-pdf">View PDF proof</a>
                : <a href={fileUrl(o.proof_file_path)} target="_blank" rel="noreferrer"><img src={fileUrl(o.proof_file_path)} alt="Proof" className="max-h-80 rounded-lg border border-border" data-testid="proof-image" /></a>}
              <div className="mt-3 text-sm text-muted-foreground space-y-1">
                {o.claimed_amount != null && <p>Customer claims paid: <strong className="text-foreground">{money(o.claimed_amount)}</strong></p>}
                {o.transaction_ref && <p>Reference: {o.transaction_ref}</p>}
                {o.customer_note && <p className="italic">"{o.customer_note}"</p>}
                {(o.proof_history?.length || 0) > 1 && <p className="text-xs">{o.proof_history.length} proof submissions on record.</p>}
              </div>
            </Card>
          )}

          <Card className="rounded-xl border-border p-5">
            <h3 className="font-semibold mb-3">Owner notes</h3>
            <Textarea value={ownerNotes} onChange={(e) => setOwnerNotes(e.target.value)} rows={2} data-testid="owner-notes-input" />
            <Button size="sm" variant="outline" className="rounded-lg mt-2" onClick={saveNotes} data-testid="save-notes-btn">Save notes</Button>
          </Card>

          <Card className="rounded-xl border-border p-5">
            <h3 className="font-semibold mb-3">Timeline</h3>
            <div className="space-y-3">
              {[...o.timeline].reverse().map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
                  <div><p className="text-sm font-medium">{t.status}</p><p className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()} · {t.by}{t.reason ? ` — ${t.reason}` : ""}</p></div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-xl border-border p-5">
            <h3 className="font-semibold mb-3">Customer</h3>
            <p className="font-medium text-sm">{o.customer_name}</p>
            <p className="text-sm text-muted-foreground">{o.customer_email}</p>
          </Card>

          <Card className="rounded-xl border-border p-5 space-y-3">
            <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" /><h3 className="font-semibold">Payment</h3></div>
            <p className="text-sm font-medium">{isCod ? "Cash on Delivery" : <span className="capitalize">{o.payment_method}</span>}</p>
            <p className="text-sm" data-testid="owner-payment-status">
              <span className={ps === "paid" ? "text-emerald-600 font-medium" : ps === "rejected" ? "text-destructive font-medium" : "text-amber-600 font-medium"}>{PAY_LABEL[ps] || ps}</span>
            </p>
            <div className="text-sm space-y-1 border-t border-border pt-2">
              {o.claimed_amount != null && <div className="flex justify-between"><span className="text-muted-foreground">Claimed paid</span><span>{money(o.claimed_amount)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Confirmed paid</span><span data-testid="confirmed-amount">{money(o.confirmed_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Remaining balance</span><span data-testid="remaining-balance">{money(o.remaining_balance != null ? o.remaining_balance : o.total)}</span></div>
            </div>
            {o.reject_reason && <p className="text-xs text-destructive">Rejected: {o.reject_reason}</p>}
            {o.reviewed_by && <p className="text-xs text-muted-foreground">Reviewed by {o.reviewed_by}</p>}
            {canReview && (
              <div className="flex flex-col gap-2 pt-2 border-t border-border">
                <Button size="sm" onClick={approve} className="rounded-lg" data-testid="approve-payment-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve payment</Button>
                <Button size="sm" variant="outline" onClick={() => { setAmount(String(o.total)); setPartial(true); }} className="rounded-lg" data-testid="partial-payment-btn"><DollarSign className="h-4 w-4 mr-1.5" /> Partial payment</Button>
                <Button size="sm" variant="outline" onClick={() => setReject(true)} className="rounded-lg text-destructive" data-testid="reject-payment-btn"><XCircle className="h-4 w-4 mr-1.5" /> Reject payment</Button>
              </div>
            )}
          </Card>

          <Card className="rounded-xl border-border p-5 space-y-2">
            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-muted-foreground" /><h3 className="font-semibold">Fulfillment</h3></div>
            <p className="text-sm capitalize font-medium">{o.fulfillment_method}</p>
            {o.fulfillment_date && <p className="text-sm text-muted-foreground">Scheduled: {o.fulfillment_date}</p>}
            {o.shipping_address && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{o.shipping_address}</p>}
            {o.notes && <p className="text-sm text-muted-foreground italic">"{o.notes}"</p>}
          </Card>
        </div>
      </div>

      <Dialog open={partial} onOpenChange={setPartial}>
        <DialogContent><DialogHeader><DialogTitle>Confirm partial payment</DialogTitle></DialogHeader>
          <Label>Confirmed amount received</Label>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="partial-amount-input" />
          <p className="text-sm text-muted-foreground">Order total {money(o.total)}. Remaining will be {money(Math.max(0, o.total - (Number(amount) || 0)))}.</p>
          <DialogFooter><Button variant="outline" onClick={() => setPartial(false)}>Cancel</Button><Button onClick={doPartial} data-testid="confirm-partial-btn">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reject} onOpenChange={setReject}>
        <DialogContent><DialogHeader><DialogTitle>Reject payment</DialogTitle></DialogHeader>
          <Label>Reason (shared with customer)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="reject-reason-input" />
          <DialogFooter><Button variant="outline" onClick={() => setReject(false)}>Cancel</Button><Button className="bg-destructive text-destructive-foreground" onClick={doReject} data-testid="confirm-reject-btn">Reject payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
